import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';

import componentStyles from './pc-header.css.lit';
import globalStyles from './pc-header.lightDom.css.lit';

import '../pc-icon/pc-icon';
import '../pc-container/pc-container';

/**
 * Header component
 * @element pc-header
 */
@customElement('pc-header')
export class PCHeader extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;
  static styles: CSSResultArray = [componentStyles];

  @state()
  isMenuExpanded = false;

  @state()
  isSearchExpanded = false;

  @query('.primary-section')
  _primarySection: HTMLDetailsElement;

  @query('.menu-trigger')
  _menuTrigger: HTMLButtonElement;

  _overlay: Element;

  _lightDOM: Element;

  isMobile: boolean;

  connectedCallback() {
    super.connectedCallback();

    const maxWidthScreen = 992;

    this._mobileMode(maxWidthScreen);
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);
    this._lightDOM = this;
    this._createOverlay();
  }

  _createOverlay() {
    let overlayElement = document.querySelector('.overlay-header');

    if (!overlayElement) {
      const bodyElement = document.querySelector('body');
      overlayElement = document.createElement('div');
      overlayElement.classList.add('overlay-header');
      bodyElement?.prepend(overlayElement);
    }

    this._overlay = overlayElement;
  }

  _mobileMode(width: number) {
    const maxMobileWidth = width - 1;
    const mediaQuery = `(max-width: ${maxMobileWidth}px)`;
    const mediaQueryList = window.matchMedia(mediaQuery);
    // Set initial value to isMobile
    this.isMobile = mediaQueryList.matches;

    // update isMobile value only when screen width is crossing maxMobileWidth (1024)
    mediaQueryList.addEventListener('change', event => {
      this.isMobile = event.matches;
      this.requestUpdate();
    });
  }

  _handleMenuTriggerClick = () => {
    // Reset children elements.
    if (this._menuTrigger.classList.contains('is-expanded')) {
      const items = [
        ...this._lightDOM.querySelectorAll('.is-hidden'),
        ...this._lightDOM.querySelectorAll('[is-expanded]'),
      ];

      items.forEach(element => {
        if (element.classList.contains('is-hidden')) {
          element.classList.remove('is-hidden');
        }

        if (element.hasAttribute('is-expanded')) {
          element.removeAttribute('is-expanded');
        }
      });
    }

    this.isMenuExpanded = !this.isMenuExpanded;

    this._primarySection.classList.toggle('is-expanded');
    this._menuTrigger.classList.toggle('is-expanded');
    this._overlay.classList.toggle('is-visible');
  };

  render(): TemplateResult {
    return html`
      <header class="header">
        <pc-container>
          <div class="content">
            <slot name="logo"></slot>

            <div class="links-section">
              <div class="utility-section">
                <slot name="utility"></slot>

                <div class="actions">
                  <button
                    class="search-trigger"
                    aria-label="Toggle search section"
                    @click=${() =>
                      (this.isSearchExpanded = !this.isSearchExpanded)}
                    aria-label="Toggle flyout menu"
                    aria-expanded=${this.isSearchExpanded ? 'true' : 'false'}
                    aria-controls="flyout-search"
                    aria-haspopup="true"
                  >
                    <pc-icon name="search" is-fixed></pc-icon>
                  </button>

                  <button
                    class="menu-trigger"
                    @click=${this._handleMenuTriggerClick}
                    aria-label="Toggle flyout menu"
                    aria-expanded=${this.isMenuExpanded ? 'true' : 'false'}
                    aria-controls="flyout-menu"
                    aria-haspopup="true"
                  >
                    <pc-icon name="bars" size="xl" is-fixed></pc-icon>
                    <pc-icon name="xmark-large" size="xl" is-fixed></pc-icon>
                  </button>
                </div>
              </div>

              <div class="primary-section" id="flyout-menu">
                <slot name="primary"></slot>
              </div>

              <div
                class="search-section
                ${this.isSearchExpanded ? 'is-expanded' : ''}
              "
                id="flyout-search"
              >
                <div class="inner">
                  <div class="search-logo">
                    <slot name="search-logo"></slot>
                  </div>

                  ${this.isMobile ? this._searchButtonCloseTemplate() : ''}

                  <div class="search">
                    <slot name="search"></slot>
                  </div>

                  ${!this.isMobile ? this._searchButtonCloseTemplate() : ''}
                </div>
              </div>
            </div>
          </div>
        </pc-container>
      </header>
    `;
  }

  _searchButtonCloseTemplate(): TemplateResult {
    return html`
      <button
        class="search-trigger"
        aria-label="Close search section"
        @click=${() => (this.isSearchExpanded = false)}
        aria-label="Toggle flyout menu"
        aria-expanded=${this.isSearchExpanded ? 'true' : 'false'}
        aria-controls="flyout-search"
        aria-haspopup="true"
      >
        <pc-icon name="close" is-fixed></pc-icon>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-header': PCHeader;
  }
}
